import { analyticsService } from "@/common/services/analytics/analytics.service";
import type { ChoiceFacet as ChoiceFacetData } from "@/features/search/api/types";
import { useSearchNavigationContext } from "@/features/search/context/SearchContext";
import { RadioButton } from "@/ui/cva/radio-button/RadioButton";

/**
 * A facet the service says is one choice out of a list — a range it decided reads better as
 * mutually exclusive options ("From 30%") than as two numeric inputs.
 *
 * Deliberately knows nothing about which facet it is rendering: the options, their labels and the
 * param to set all come from the response, so iOS and Android answer the same question with the
 * same words, and a second choice facet needs no code here. Picking the selected option again
 * clears it, which saves an "any" row that would need its own string.
 */
export const ChoiceFacet = ({ facet }: { facet: ChoiceFacetData }) => {
  const { setParam } = useSearchNavigationContext();

  const choose = (value: string, isSelected: boolean) => {
    const next = isSelected ? null : value;

    setParam(facet.param, next);
    analyticsService.trackFilterClickList(facet.key, next);
  };

  return (
    <ul className="flex flex-col space-y-2">
      {facet.values.map(value => (
        <li key={value.value}>
          <label className="flex cursor-pointer items-center space-x-2 font-body text-sm">
            <RadioButton
              name={facet.key}
              checked={value.selected}
              /* Selection runs off the click so that clicking the selected option can clear it —
               * `change` never fires for a radio that is already on. Controlled input, hence the
               * empty onChange React asks for. */
              onChange={() => {}}
              onClick={() => choose(value.value, value.selected)}
            />
            <span className="text-black">{value.label}</span>
          </label>
        </li>
      ))}
    </ul>
  );
};
